import React, { useCallback, useRef } from "react";
import { mutate } from "swr";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useForm, useFormContext } from "react-hook-form";

import { useToastPromise } from "@/hooks/useToast";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
  AlertDialogFooter,
  Button,
} from "@chakra-ui/react";

type FormInputs = {
  message: string;
};

type Props = {
  id?: number;
  isOpen: boolean;
  onClose: () => void;
  mutateData: () => Promise<any>;
};

const SendRequestDialog = ({
  id,
  isOpen,
  onClose,
  mutateData,
}: Props): React.ReactElement => {
  const dialogRef = useRef();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();
  const { t } = useTranslation("common");
  const toast = useToastPromise();

  const { setValue } = useFormContext();

  const onSubmit = (data) => {
    return toast.promise(
      axios
        .post("/office-request/editorial", {
          ...data,
          id,
        })
        .then(() => {
          onClose();
          mutateData();
          mutate("/editorial-office");
          mutate("/user/editorial-office");
          mutate("/office-request");
          mutate("/office-request/requests");
          mutate(`/office-request/office-requests`);
          mutate("/office-request/office-invitations");
        })
    );
  };

  const onCancel = useCallback(() => {
    setValue("editorialOffice", null);
    onClose();
  }, []);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={dialogRef}
      onClose={onCancel}
      isCentered
      preserveScrollBarGap
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t(
                "editorialOffice.accreditationInformation.invitationSendTitle"
              )}
            </AlertDialogHeader>

            <AlertDialogBody>
              <FormControl>
                <FormLabel>
                  {t("editorialOffice.accreditationInformation.invitationSend")}
                </FormLabel>
              </FormControl>
              <FormControl isInvalid={!!errors.message} isRequired>
                <FormLabel htmlFor="message">
                  {t(
                    "editorialOffice.accreditationInformation.messageToOffice"
                  )}
                </FormLabel>
                <Textarea
                  type="text"
                  {...register("message", { required: true })}
                  ref={dialogRef}
                />
                {errors.message && (
                  <FormErrorMessage>
                    {t("users.fieldRequired")}
                  </FormErrorMessage>
                )}
              </FormControl>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button variant="ghost" onClick={onCancel}>
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                colorScheme="green"
                ml={3}
              >
                {t("send")}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default SendRequestDialog;
